<template>
  <Teleport to="body">
    <Transition name="modal" appear>
      <div 
        v-if="isVisible" 
        class="fixed inset-0 z-50 overflow-y-auto"
        @click="handleBackdropClick"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
        
        <!-- Modal Container -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div 
            ref="confirmRef"
            class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transition-all w-full max-w-md mx-auto"
            @click.stop
          >
            <!-- Header -->
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div class="flex items-center">
                <!-- Icon -->
                <div :class="iconClasses" class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-4">
                  <component :is="iconComponent" class="w-6 h-6" />
                </div>
                
                <!-- Title -->
                <h3 class="text-lg font-medium text-gray-900 dark:text-white">
                  {{ confirmData.title || 'Confirmação' }}
                </h3>
              </div>
            </div>
            
            <!-- Body -->
            <div class="px-6 py-4">
              <p class="text-gray-700 dark:text-gray-300">
                {{ confirmData.message }}
              </p>
            </div>
            
            <!-- Footer -->
            <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div class="flex justify-end space-x-3">
                <button 
                  @click="cancel"
                  type="button"
                  class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {{ confirmData.cancelText || 'Cancelar' }}
                </button>
                <button 
                  @click="confirm"
                  type="button"
                  :class="[
                    'px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors',
                    confirmButtonClasses
                  ]"
                >
                  {{ confirmData.confirmText || 'Confirmar' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useGlobalConfirm } from '@/composables/useGlobalConfirm'

// Icons
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon
} from '@heroicons/vue/24/outline'

const confirmRef = ref<HTMLElement>()
const { isVisible, confirmData, closeConfirm } = useGlobalConfirm()

const iconComponent = computed(() => {
  switch (confirmData.type) {
    case 'warning':
      return ExclamationTriangleIcon
    case 'info':
      return InformationCircleIcon
    case 'success':
      return CheckCircleIcon
    case 'error':
      return XCircleIcon
    case 'question':
    default:
      return QuestionMarkCircleIcon
  }
})

const iconClasses = computed(() => {
  switch (confirmData.type) {
    case 'warning':
      return 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400'
    case 'info':
      return 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
    case 'success':
      return 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400'
    case 'error':
      return 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400'
    case 'question':
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
  }
})

const confirmButtonClasses = computed(() => {
  switch (confirmData.type) {
    case 'warning':
      return 'bg-yellow-600 hover:bg-yellow-700'
    case 'info':
      return 'bg-blue-600 hover:bg-blue-700'
    case 'success':
      return 'bg-green-600 hover:bg-green-700'
    case 'error':
      return 'bg-red-600 hover:bg-red-700'
    case 'question':
    default:
      return 'bg-blue-600 hover:bg-blue-700'
  }
})

const confirm = () => {
  closeConfirm(true)
}

const cancel = () => {
  closeConfirm(false)
}

const handleBackdropClick = () => {
  if (confirmData.closeOnBackdrop !== false) {
    cancel()
  }
}

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && confirmData.closeOnEscape !== false && isVisible.value) {
    cancel()
  }
}

// Focus management
watch(isVisible, async (visible) => {
  if (visible) {
    await nextTick()
    confirmRef.value?.focus()
    document.addEventListener('keydown', handleEscapeKey)
  } else {
    document.removeEventListener('keydown', handleEscapeKey)
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}

.modal-enter-from .relative {
  transform: scale(0.95) translateY(-20px);
}

.modal-leave-to .relative {
  transform: scale(0.95) translateY(20px);
}
</style>